/** * Table component for displaying and managing assignments * Supports CRUD
operations and quota management for credit-based entitlements */
<template>
  <va-card class="card-container">
    <!-- Header with title and create button -->
    <va-card-title>
      <div class="assignments-header">
        <slot name="title">
          {{ $t("assignments.title") }}
        </slot>
        <va-button
          class="ml-2"
          color="primary"
          @click="openCreateAssignmentModal"
        >
          {{ $t("buttons.createAssignment") }}
        </va-button>
      </div>
    </va-card-title>

    <!-- Assignments data table -->
    <va-card-content>
      <va-data-table
        :no-data-html="$t('assignments.noItems')"
        :items="state.assignments"
        :columns="columns"
      >
        <!-- Assignee column with badges and copy button -->
        <template #header(assignee)>{{ $t("assignments.assignee") }}</template>
        <template #cell(assignee)="{ source: assignee }">
          <span class="badges">
            <router-link :to="{ path: identityLink(assignee) }">
              {{ assignee.email ?? assignee.name }}
            </router-link>

            <va-badge
              v-if="assignee.type === 'Invite'"
              :text="$t('assignments.invite')"
              color="#8093F1"
            />

            <va-badge
              v-else-if="assignee.type === 'User'"
              :text="$t('assignments.user')"
              color="#B3B3A8"
            />

            <button-clipboard-copy
              :field="$t('assignments.email')"
              :value="assignee.email ?? assignee.name"
            />
          </span>
        </template>

        <!-- Name column -->
        <template #header(name)>{{ $t("assignments.name") }}</template>
        <template #cell(name)="{ source: name }">
          <span v-if="name">{{ name }}</span>
          <span v-else class="text-muted">-</span>
        </template>

        <!-- Created at column -->
        <template #header(createdAt)>{{
          $t("assignments.createdAt")
        }}</template>
        <template #cell(createdAt)="{ source: createdAt }">
          {{ printDate(createdAt) }}
        </template>

        <!-- Allowance column (only for credit-based entitlements) -->
        <template #header(allowance)>{{
          $t("assignments.allowance")
        }}</template>
        <template #cell(allowance)="{ source: allowance }">
          {{ allowance }}
        </template>

        <!-- Remaining Allowance column (only for credit-based entitlements) -->
        <template #header(remainingAllowance)>{{
          $t("assignments.remainingAllowance")
        }}</template>
        <template #cell(remainingAllowance)="{ source: remainingAllowance }">
          {{ remainingAllowance }}
        </template>

        <!-- Actions column with edit and delete buttons -->
        <template #header(actions)>{{ $t("assignments.actions") }}</template>
        <template #cell(actions)="{ rowIndex }">
          <div class="action-buttons">
            <va-button
              v-if="hasUsageQuota"
              color="primary"
              flat
              icon="edit"
              @click="openEditQuotaModal(rowIndex)"
            />
            <va-button
              v-if="!isRestrictedNVivoTrial(entitlement)"
              color="#DC2626"
              flat
              icon="delete"
              @click="openUnassignModal(rowIndex)"
            />
          </div>
        </template>
      </va-data-table>
    </va-card-content>

    <!-- Pagination -->
    <va-card-content>
      <va-pagination
        class="pagination"
        input
        color="#000000"
        v-model="state.currentPage"
        :hide-on-single-page="true"
        :pages="state.totalPages"
        :page-size="state.perPage"
      />
    </va-card-content>
  </va-card>

  <!-- Create assignment modal -->
  <create-assignment-modal
    v-if="state.showCreateAssignmentModal"
    :is-open="state.showCreateAssignmentModal"
    :entitlement-id="entitlementId"
    :has-usage-quota="hasUsageQuota"
    @cancel="closeCreateAssignmentModal"
    @created="refreshAssignments"
  />

  <!-- Edit assignment quota modal -->
  <edit-assignment-quota-modal
    v-if="state.showEditQuotaModal"
    :is-open="state.showEditQuotaModal"
    :assignment-id="state.editingAssignmentId"
    :initial-allowance="state.editingAllowance"
    :initial-remaining-allowance="state.editingRemainingAllowance"
    @cancel="closeEditQuotaModal"
    @updated="refreshAssignments"
  />
</template>

<style scoped>
/* Header styling */
.assignments-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

/* Badge container styling */
.badges {
  display: flex;
  gap: 5px;
  align-items: center;
}

/* Action buttons container */
.action-buttons {
  display: flex;
  gap: 0.5rem;
}

/* Badge styling */
.badge {
  display: inline-flex;
  align-items: center;
  background-color: #dc2626;
  color: white;
  padding: 0.5em;
  border-radius: 12px;
  cursor: pointer;
}

.badge-content {
  margin-right: 0.5em;
}

.icon-delete {
  font-size: 16px;
}
</style>

<script setup>
import { reactive, watch, onMounted, inject, computed } from "vue";
import { callFetchAssignments, callDeleteAssignmen } from "@/api/licensing";
import { useI18n } from "vue-i18n";
import ButtonClipboardCopy from "@/components/ButtonClipboardCopy.vue";
import CreateAssignmentModal from "@/components/modal/CreateAssignmentModal.vue";
import EditAssignmentQuotaModal from "@/components/modal/EditAssignmentQuotaModal.vue";
import { printDate, isRestrictedNVivoTrial } from "@/utils";
import { identityLink } from "@/utils/identity";
import { useStore } from "vuex";

// Global services and utilities
const { t } = useI18n();
const toasts = inject("toasts");
const handleError = inject("handleError");
const store = useStore();

/**
 * Component props
 * @property {string} entitlementId - ID of the entitlement
 */
const props = defineProps({
  entitlementId: String
});

/**
 * Computed property to check if entitlement has usage quota
 * Determines whether to show quota-related columns and actions
 */
const hasUsageQuota = computed(() => {
  const entitlement = store.state.licensing.entitlementEdit;
  return entitlement?.usage?.type === "Credits";
});

/**
 * Access entitlement data from store
 */
const entitlement = computed(() => store.state.licensing.entitlementEdit);

/**
 * Table columns configuration
 * Adds quota columns only for credit-based entitlements
 */
const columns = computed(() => {
  const baseColumns = ["assignee", "name", "createdAt"];

  if (hasUsageQuota.value) {
    return [...baseColumns, "allowance", "remainingAllowance", "actions"];
  }

  // For non-quota entitlements, only show actions if not a restricted NVivo trial
  const shouldShowActions = !isRestrictedNVivoTrial(entitlement.value);
  return shouldShowActions ? [...baseColumns, "actions"] : baseColumns;
});

/**
 * Component state
 */
const state = reactive({
  assignments: [],
  loading: false,
  lastLoadedPage: undefined,
  currentPage: 1,
  totalItems: 0,
  perPage: 100,
  totalPages: 0,
  showCreateAssignmentModal: false,
  showEditQuotaModal: false,
  editingAssignmentId: "",
  editingAllowance: 0,
  editingRemainingAllowance: 0,
  unassigngId: ""
});

/**
 * Loads assignments data for the current page
 * @param {number} page - Page number to load
 * @param {boolean} force - Force reload even if page is already loaded
 */
async function loadAssignments(page = 1, force = false) {
  if ((state.loading === true || page === state.lastLoadedPage) && !force) {
    return;
  }

  state.lastLoadedPage = page;
  state.loading = true;

  const params = {
    count: state.perPage,
    page,
    entitlementId: props.entitlementId
  };

  try {
    const resp = await callFetchAssignments({ params });

    if (resp?.data) {
      state.assignments = resp.data.items.map(item => ({
        id: item.id,
        assignee: item.assignee,
        name: item.assignee?.name || item.assignee?.displayName || null,
        createdAt: item.createdAt,
        allowance: item.usageQuota?.allowance,
        remainingAllowance: item.usageQuota?.remainingAllowance
      }));

      updatePagination(resp.data.total, page);
    }
  } catch (e) {
    handleError(e?.response?.data);
  }

  state.loading = false;
}

/**
 * Updates pagination state
 * @param {number} totalItems - Total number of items
 * @param {number} page - Current page number
 */
function updatePagination(totalItems, page = 1) {
  state.totalItems = totalItems;
  state.currentPage = page;
  state.totalPages = Math.ceil(totalItems / state.perPage);
}

/**
 * Opens the edit quota modal and prepares for editing
 * @param {number} rowIndex - Index of the row to edit
 */
const openEditQuotaModal = rowIndex => {
  const assignment = state.assignments[rowIndex];
  state.editingAssignmentId = assignment.id;
  state.editingAllowance = assignment.allowance;
  state.editingRemainingAllowance = assignment.remainingAllowance;
  state.showEditQuotaModal = true;
};

/**
 * Closes the edit quota modal and resets state
 */
const closeEditQuotaModal = () => {
  state.showEditQuotaModal = false;
  state.editingAssignmentId = "";
  state.editingAllowance = 0;
  state.editingRemainingAllowance = 0;
};

/**
 * Opens the unassign modal and initiates unassignment process
 * @param {number} rowIndex - Index of the row to unassign
 */
const openUnassignModal = rowIndex => {
  state.unassigngId = state.assignments[rowIndex].id;
  unassignItem();
};

/**
 * Handles the unassignment process
 */
const unassignItem = async () => {
  try {
    await callDeleteAssignmen(state.unassigngId);
    const itemIndex = state.assignments.findIndex(
      el => el.id === state.unassigngId
    );
    state.assignments.splice(itemIndex, 1);
    toasts?.info(t("assignments.unassignSuccess"));
  } catch (err) {
    toasts?.error(err);
  }
  state.unassigngId = "";
};

/**
 * Opens the create assignment modal
 */
const openCreateAssignmentModal = () => {
  state.showCreateAssignmentModal = true;
};

/**
 * Closes the create assignment modal
 */
const closeCreateAssignmentModal = () => {
  state.showCreateAssignmentModal = false;
};

/**
 * Refreshes the assignments list
 * Resets pagination and forces a reload
 */
const refreshAssignments = async () => {
  state.currentPage = 1;
  state.lastLoadedPage = undefined;
  await loadAssignments(1, true);
};

// Watch for page changes
watch(
  () => state.currentPage,
  async currentPage => {
    await loadAssignments(currentPage);
  }
);

// Initial data load
onMounted(async () => {
  await loadAssignments();
});

// Expose refresh method to parent components
defineExpose({
  refreshAssignments
});
</script>
